/**
 * Theme Enforcer
 * 
 * This utility forces theme variables on elements that might have hardcoded styles
 * by adding appropriate classes or inline styles to ensure theme consistency
 */

// List of selectors that need to have their background colors adjusted
const bgSelectors = [
  '.bg-white', 
  '.bg-gray-50', 
  '.bg-gray-100', 
  'thead', 
  'tbody',
  '.card',
  '.card-body',
  '.card-header',
  '.modal-content',
  '.modal-header',
  '.modal-body',
  '.modal-footer',
  '.dropdown-menu',
  // Added selectors
  '.shadow',
  '.rounded-lg',
  '.tab-content',
  '.nav-tabs .nav-link',
  'button.px-3.py-1.rounded',
  '.nav-tabs',
  // Portfolio specific selectors
  'tr.bg-gray-50',
  'tr.bg-white',
  '.min-w-full thead',
  '.min-w-full tbody',
  '.min-w-full tbody tr',
  '.shadow-md',
  '.bg-gray-600',
  '.portfolio-content'
];

// List of selectors that need to have their text colors adjusted
const textSelectors = [
  '.text-gray-500',
  '.text-gray-600',
  '.text-gray-700',
  '.text-gray-900', 
  '.text-black',
  '.table-responsive th',
  '.table-responsive td',
  // Added selectors
  '.text-xl',
  '.text-lg',
  '.text-sm',
  '.font-medium',
  '.font-semibold',
  'button.text-gray-600',
  // Portfolio specific selectors
  '.text-3xl',
  '.text-2xl',
  '.text-xs',
  '.font-bold',
  '.whitespace-nowrap',
  'td.px-6.py-4'
];

// List of selectors that need to have their border colors adjusted
const borderSelectors = [
  '.border',
  '.border-gray-200',
  '.border-gray-300', 
  '.border-top',
  '.border-right',
  '.border-bottom',
  '.border-left',
  '.table-bordered',
  '.table-bordered th',
  '.table-bordered td',
  // Added selectors
  '.rounded-lg',
  'button',
  '.nav-tabs',
  'input',
  'select',
  'textarea',
  // Portfolio specific selectors
  '.border-b',
  '.border-t',
  '.divide-y',
  '.divide-gray-200'
];

// Run this function to enforce themes across all components
export const enforceTheme = () => {
  // Run initially and then observe DOM changes
  applyThemeStyles();
  
  // Set up a MutationObserver to watch for DOM changes
  const observer = new MutationObserver((mutations) => {
    // Delay the execution to batch multiple changes
    setTimeout(applyThemeStyles, 50);
  });
  
  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style']
  });
  
  // Return the observer so it can be disconnected if needed
  return observer;
};

// Apply theme styles to elements
function applyThemeStyles() {
  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
  
  // Apply background colors
  bgSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(element => {
      (element as HTMLElement).style.backgroundColor = `var(--${selector.includes('header') || selector.includes('thead') ? 'table-header-bg' : 'card-bg'})`;
      (element as HTMLElement).style.transition = 'background-color 0.3s ease';
    });
  });
  
  // Apply text colors
  textSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(element => {
      (element as HTMLElement).style.color = `var(--${selector.includes('500') || selector.includes('600') ? 'text-secondary' : 'text-primary'})`;
      (element as HTMLElement).style.transition = 'color 0.3s ease';
    });
  });
  
  // Apply border colors
  borderSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(element => {
      (element as HTMLElement).style.borderColor = 'var(--border-color)';
      (element as HTMLElement).style.transition = 'border-color 0.3s ease';
    });
  });
  
  // Target specific components
  
  // Bootstrap tabs
  document.querySelectorAll('.nav-tabs .nav-link.active').forEach(element => {
    (element as HTMLElement).style.backgroundColor = 'var(--accent)';
    (element as HTMLElement).style.color = isDarkMode ? 'var(--text-primary)' : 'white';
  });
  
  // Portfolio page cards
  document.querySelectorAll('.bg-gray-50').forEach(element => {
    (element as HTMLElement).style.backgroundColor = 'var(--secondary-bg)';
  });
  
  // Portfolio tables
  document.querySelectorAll('.min-w-full thead, thead.bg-gray-50').forEach(element => {
    (element as HTMLElement).style.backgroundColor = 'var(--table-header-bg)';
    (element as HTMLElement).style.color = 'var(--text-primary)';
  });
  
  document.querySelectorAll('.min-w-full tbody, tbody.bg-white, tr.bg-gray-50, tr.bg-white').forEach(element => {
    (element as HTMLElement).style.backgroundColor = 'var(--card-bg)';
    (element as HTMLElement).style.color = 'var(--text-primary)';
  });
  
  document.querySelectorAll('td.whitespace-nowrap, th.whitespace-nowrap').forEach(element => {
    (element as HTMLElement).style.color = 'var(--text-primary)';
  });
  
  // Portfolio main container
  document.querySelectorAll('.max-w-7xl').forEach(element => {
    (element as HTMLElement).style.backgroundColor = 'var(--primary-bg)';
    (element as HTMLElement).style.color = 'var(--text-primary)';
  });
  
  // Report buttons
  document.querySelectorAll('button.px-3.py-1.rounded').forEach(element => {
    const button = element as HTMLElement;
    if (button.classList.contains('bg-white')) {
      button.style.backgroundColor = 'var(--card-bg)';
      button.style.color = 'var(--text-primary)';
    }
  });
}

export default enforceTheme; 